import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Search, X, LayoutGrid, List, Eye, EyeOff, User, UserCheck, Users } from 'lucide-react'
import { useClientes } from '@/hooks/useClientes'
import { useUsuariosOrg } from '@/hooks/useUsuarios'

/**
 * El mismo interruptor "Míos / Todo el estudio" del calendario. `mios` significa lo mismo en las
 * dos pantallas: la tarea asignada a mí. "Asigné" mira `asignado_por`, que se separa de
 * `created_by` cuando alguien reasigna una tarea que no creó, y por eso sobrevive como tercera
 * posición en vez de quedar tapada por "Míos".
 */
export type FiltroScope = 'mios' | 'asignadas_por_mi' | 'estudio'

interface FiltrosTareasProps {
  busqueda: string
  onBusquedaChange: (value: string) => void
  asignadoA: string
  onAsignadoAChange: (value: string) => void
  clienteId: string
  onClienteIdChange: (value: string) => void
  vistaKanban: boolean
  onVistaChange: (kanban: boolean) => void
  filtroScope?: FiltroScope
  onFiltroScopeChange?: (scope: FiltroScope) => void
  hideAsignadoFilter?: boolean
  mostrarCompletadas?: boolean
  onMostrarCompletadasChange?: (value: boolean) => void
  // Filtro de rango temporal para completadas
  completadasDesde?: string | null
  onCompletadasDesdeChange?: (value: string | null) => void
  completadasHasta?: string | null
  onCompletadasHastaChange?: (value: string | null) => void
}

const SCOPES: { value: FiltroScope; label: string; icono: typeof User }[] = [
  { value: 'mios', label: 'Míos', icono: User },
  { value: 'asignadas_por_mi', label: 'Asigné', icono: UserCheck },
  { value: 'estudio', label: 'Todo el estudio', icono: Users },
]

export function FiltrosTareas({
  busqueda,
  onBusquedaChange,
  asignadoA,
  onAsignadoAChange,
  clienteId,
  onClienteIdChange,
  vistaKanban,
  onVistaChange,
  filtroScope = 'mios',
  onFiltroScopeChange,
  hideAsignadoFilter = false,
  mostrarCompletadas = false,
  onMostrarCompletadasChange,
  completadasDesde,
  onCompletadasDesdeChange,
  completadasHasta,
  onCompletadasHastaChange,
}: FiltrosTareasProps) {
  const { data: clientes = [] } = useClientes({ estado: 'Activo' })
  const { data: usuarios = [] } = useUsuariosOrg({ activo: true })

  // Limpiar deja el alcance más amplio, que ahora es "Todo el estudio": el default de entrada es
  // "Míos" (ORR-398) y arrastrar el default acá dejaría un botón "Limpiar" que sigue filtrando.
  // Sin interruptor el alcance es fijo, y no cuenta como filtro que se pueda limpiar.
  const hayFiltrosActivos =
    busqueda || asignadoA || clienteId || (onFiltroScopeChange && filtroScope !== 'estudio')

  const limpiarFiltros = () => {
    onBusquedaChange('')
    onAsignadoAChange('')
    onClienteIdChange('')
    onFiltroScopeChange?.('estudio')
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Búsqueda */}
        <div className="relative flex-1 min-w-[180px] max-w-full sm:max-w-[300px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar tareas..."
            value={busqueda}
            onChange={(e) => onBusquedaChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Alcance: Míos / Asigné / Todo el estudio. Mismo control segmentado que el calendario
            (ORR-367), para que el interruptor se lea igual en las dos pantallas. */}
        {onFiltroScopeChange && (
          <div className="flex items-center gap-1 rounded-md border p-0.5 w-fit">
            {SCOPES.map(({ value, label, icono: Icono }) => (
              <Button
                key={value}
                variant={filtroScope === value ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => onFiltroScopeChange(value)}
                className="h-7 text-xs"
                aria-pressed={filtroScope === value}
              >
                <Icono className="h-3.5 w-3.5 mr-1.5" />
                {label}
              </Button>
            ))}
          </div>
        )}

        {/* Asignado a */}
        {!hideAsignadoFilter && (
          <Select value={asignadoA || '_all'} onValueChange={(v) => onAsignadoAChange(v === '_all' ? '' : v)}>
            <SelectTrigger className="hidden sm:flex w-[180px]">
              <SelectValue placeholder="Asignado a" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todos los usuarios</SelectItem>
              {usuarios.map((usuario) => (
                <SelectItem key={usuario.id} value={usuario.email}>
                  {usuario.nombre || usuario.email.split('@')[0]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Cliente */}
        <Select value={clienteId || '_all'} onValueChange={(v) => onClienteIdChange(v === '_all' ? '' : v)}>
          <SelectTrigger className="hidden sm:flex w-[180px]">
            <SelectValue placeholder="Cliente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todos los clientes</SelectItem>
            {clientes.map((cliente) => (
              <SelectItem key={cliente.id ?? ''} value={cliente.id ?? ''}>{cliente.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Toggle completadas */}
        {onMostrarCompletadasChange && (
          <Button
            variant={mostrarCompletadas ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => onMostrarCompletadasChange(!mostrarCompletadas)}
            className="gap-1.5"
          >
            {mostrarCompletadas ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span className="hidden sm:inline">Completadas</span>
          </Button>
        )}

        {/* Limpiar filtros */}
        {hayFiltrosActivos && (
          <Button variant="ghost" size="sm" onClick={limpiarFiltros}>
            <X className="h-4 w-4 mr-1" />
            Limpiar
          </Button>
        )}

        {/* Toggle Vista */}
        <div className="ml-auto hidden md:flex items-center gap-2">
          <div className="flex items-center border rounded-lg">
            <Button variant={vistaKanban ? 'default' : 'ghost'} size="sm" onClick={() => onVistaChange(true)} className="rounded-r-none">
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button variant={!vistaKanban ? 'default' : 'ghost'} size="sm" onClick={() => onVistaChange(false)} className="rounded-l-none">
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Rango temporal de completadas */}
      {mostrarCompletadas && onCompletadasDesdeChange && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Completadas desde</span>
          <Input
            type="date"
            value={completadasDesde ?? ''}
            onChange={(e) => onCompletadasDesdeChange(e.target.value || null)}
            className="h-8 w-auto"
          />
          <span className="text-muted-foreground">hasta</span>
          <Input
            type="date"
            value={completadasHasta ?? ''}
            onChange={(e) => onCompletadasHastaChange?.(e.target.value || null)}
            className="h-8 w-auto"
          />
        </div>
      )}
    </div>
  )
}
